import React, { useContext, useEffect, useState } from "react";

import { RegistrationFormProps } from "@/common/Outro/RegistrationForm/RegistrationForm";
import {
  FormButton,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormRadio,
  withForm,
} from "@/components/Form";
import {
  FormField,
  FormFields,
  FormFieldType,
  getAllFieldsInArray,
  getAvailableFormFields,
  isValid,
} from "@/components/Form/FormFields";
import FormSelect from "@/components/Form/Select";
import { GlobalContext } from "@/contexts";
import LocaleContext from "@/i18n";
import { CustomField, RegistrationStatus } from "@/service/Model";
import TrackingService from "@/service/Tracking";
import Svg from "@/styles/utils/Svg";
import { isMobilePhone } from "@/utils/getters";

import { OutroFeedback, OutroFeedbackIcon } from "../../../styles";
import { RegistrationStep, Title } from "./styles";

interface FieldData {
  value?: string;
  checked?: boolean;
  valid: boolean;
}

interface FormData {
  [fieldName: string]: FieldData;
}

const CUSTOM_FIELDS = "customFields";

// [page][row][column]
const initFormPages = (
  formFields: FormFields,
  props: RegistrationFormProps
): FormField[][][] => {
  const {
    outro: { advertisementOptIn, hansIdOptIn, lightRegistration, customFields },
    hansId,
    hansIdExists,
  } = props;
  const pages: FormField[][][] = [];
  const showHansIdOptIn = hansIdOptIn && hansId && !hansIdExists;

  if (lightRegistration) {
    const personalInformation = [
      [formFields.outroTitle],
      [formFields.firstName, formFields.lastName],
      [formFields.email],
    ];

    if (advertisementOptIn) {
      personalInformation.push([formFields.advertisementOptIn]);
    }

    if (showHansIdOptIn) {
      personalInformation.push([formFields.hansIdOptIn]);
    }

    personalInformation.push([formFields.infoText]);

    pages.push(personalInformation);
  } else {
    const personalInformation = [
      [formFields.outroTitle],
      [formFields.gender],
      [formFields.firstName, formFields.lastName],
      [formFields.email],
    ];

    if (customFields) {
      customFields.forEach((customField: CustomField, index: number) => {
        // insert at 1
        personalInformation.splice(1, 0, [
          {
            name: `${CUSTOM_FIELDS}-${index}`,
            label: customField.label,
            type: customField.type,
            options: customField.options,
            validation: {
              required: true, // Custom field are always required (could be a setting).
            },
          },
        ]);
      });
    }

    const addressInformation = [
      [formFields.prizeText],
      [formFields.street, formFields.houseNumber, formFields.postalcode],
      [formFields.city, formFields.telephone],
    ];

    if (advertisementOptIn) {
      addressInformation.push([formFields.advertisementOptIn]);
    }

    if (showHansIdOptIn) {
      addressInformation.push([formFields.hansIdOptIn]);
    }

    addressInformation.push([formFields.infoText]);

    if (isMobilePhone()) {
      pages.push(personalInformation.concat(addressInformation));
    } else {
      pages.push(personalInformation);
      pages.push(addressInformation);
    }
  }

  return pages;
};

const ClassicRegistrationForm: React.FC<RegistrationFormProps> = (
  props: RegistrationFormProps
): React.JSX.Element => {
  const {
    outro: { termsAndConditionsLink, privacyPolicyLink, outroTitle },
    registrationStatus,
  } = props;

  const localeContext = useContext(LocaleContext);
  const globalContext = useContext(GlobalContext);
  const { translation } = localeContext;
  const { icons } = globalContext;

  const [activePage, setActivePage] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({});

  const pages = initFormPages(getAvailableFormFields(translation), props);

  useEffect(() => {
    TrackingService.getInstance().trackOnOutroImpression();
  }, []);

  const renderFormStatus = (status: RegistrationStatus): React.JSX.Element => {
    let icon = null;
    let feedback = translation.request.pending;

    if (status === RegistrationStatus.Error) {
      icon = icons.Fail;
      feedback = translation.request.error;
    } else if (status === RegistrationStatus.Success) {
      icon = icons.Success;
      feedback = translation.thankYou;
    }

    return (
      <OutroFeedback data-qa="registration.outro.feedback">
        {icon && (
          <OutroFeedbackIcon $success={status === RegistrationStatus.Success}>
            <Svg contents={icon} height={65} width={65} />
          </OutroFeedbackIcon>
        )}
        <h3>{feedback}</h3>
        {formData.advertisementOptIn?.value &&
          status === RegistrationStatus.Success && (
            <h4>{translation.confirmRegistration}</h4>
          )}
      </OutroFeedback>
    );
  };

  const renderForm = (): React.JSX.Element => {
    const isLastStep = pages.length === activePage + 1;
    const page = pages[activePage];

    return (
      <RegistrationStep>
        {page.map((row: FormField[], rowIndex: number) => (
          <React.Fragment key={`page-${activePage}-row-${rowIndex}`}>
            {renderRowElement(row)}
          </React.Fragment>
        ))}
        {isLastStep && (
          <FormButton
            type={"submit"}
            disabled={!validatePages()}
            label={translation.registration.submit}
            onClick={handleSubmit}
            dataQA="registration.submit"
          />
        )}
        {!isLastStep && (
          <FormButton
            type={"button"}
            disabled={!validatePages(0)}
            label={translation.registration.next}
            onClick={handleNextPage}
            dataQA="registration.next"
          />
        )}
      </RegistrationStep>
    );
  };

  const renderRowElement = (row: FormField[]): React.JSX.Element => {
    return (
      <FormGroup>
        {row.map((field: FormField, columnIndex: number) => {
          const fieldData = formData[field.name] ?? { valid: true };
          return (
            <React.Fragment key={`${field.name}-${columnIndex}`}>
              {renderFieldElement(field, fieldData)}
            </React.Fragment>
          );
        })}
      </FormGroup>
    );
  };

  const renderFieldElement = (
    field: FormField,
    fieldData: FieldData
  ): React.JSX.Element | null => {
    switch (field.type) {
      case FormFieldType.Input:
        return (
          <FormInput
            name={field.name}
            label={field.label}
            required={{
              active: !!field.validation?.required, // unused
              text: translation.registration.required,
            }}
            value={fieldData.value ?? ""}
            onChange={(value: string) => handleChange(value, field)}
            valid={fieldData.valid}
          />
        );
      case FormFieldType.Select:
        return (
          <FormSelect
            name={field.name}
            label={field.label ?? ""}
            required={{
              active: !!field.validation?.required, // unused
              text: translation.registration.required,
            }}
            value={fieldData.value ?? ""}
            options={field.options ?? []}
            onChange={(value: string) => handleChange(value, field)}
            valid={fieldData.valid}
          />
        );
      case FormFieldType.Radio:
        return (
          <FormGroup $fieldType={field.type}>
            <FormRadio
              name={field.name}
              label={field.label ?? ""}
              options={field.options ?? []}
              currentValue={fieldData.value}
              onChange={(value: string) => handleChange(value, field)}
              valid={fieldData.valid}
            />
          </FormGroup>
        );
      case FormFieldType.Checkbox:
        return (
          <FormCheckbox
            icons={icons}
            name={field.name}
            label={field.label ?? ""}
            onChange={(checked: boolean) => handleChange(checked, field)}
            checked={!!fieldData.checked}
          />
        );
      case FormFieldType.Static:
        return field.element
          ? field.element(termsAndConditionsLink, privacyPolicyLink, outroTitle)
          : null;
      default:
        return null;
    }
  };

  const handleChange = (value: string | boolean, field: FormField): void => {
    const clonedFormData = { ...formData };

    if (typeof value === "boolean") {
      clonedFormData[field.name] = {
        checked: value,
        valid: true, // Hint: required is not checked for checkboxes
      };
    } else {
      clonedFormData[field.name] = {
        value,
        valid: isValid(field, value),
      };
    }
    setFormData(clonedFormData);
  };

  const validatePages = (pageIndex?: number): boolean => {
    if (!Object.keys(formData).length) return false;

    const formFields = getAllFieldsInArray(
      pageIndex !== undefined ? pages[pageIndex] : pages
    );

    return formFields.every((formField: FormField) =>
      isValid(formField, formData[formField.name]?.value ?? "")
    );
  };

  const handleNextPage = (): void => {
    setActivePage(activePage + 1);
  };

  const convertFormData = (): Record<string, string | boolean> => {
    const payload: any = {};
    Object.keys(formData).forEach((fieldName: string) => {
      let value: any = "";
      if (formData[fieldName].value !== undefined) {
        value = formData[fieldName].value;
      } else if (formData[fieldName].checked !== undefined) {
        value = formData[fieldName].checked;
      }
      if (fieldName.startsWith(CUSTOM_FIELDS)) {
        payload[CUSTOM_FIELDS] = payload[CUSTOM_FIELDS] ?? [];
        payload[CUSTOM_FIELDS].push(value);
      } else {
        payload[fieldName] = value;
      }
    });

    return payload;
  };

  const handleSubmit = async (): Promise<void> => {
    const { onSubmit } = props;
    if (!onSubmit) {
      return;
    }

    onSubmit(convertFormData());
    TrackingService.getInstance().trackOnOutroClick();
  };

  return (
    <React.Fragment>
      <Title>{translation.registerAgain}</Title>
      {!registrationStatus || registrationStatus === RegistrationStatus.None
        ? renderForm()
        : renderFormStatus(registrationStatus)}
    </React.Fragment>
  );
};

export default withForm(ClassicRegistrationForm);
